import type { LazyQueryExecFunction, OperationVariables } from '@apollo/client';
import type { UseNavigateResult } from '@tanstack/react-router';
import { get } from 'lodash';
import { toast } from 'react-toastify';
import { AUDIO, HOME_PATH, IMAGE, PDF, THUMBNAIL_FORMAT_SLUG, VIDEO, VISUAL_MODE } from 'src/constants';
import viewStateVar from 'src/store/viewState';

export const contextMenuDisable = (e: MouseEvent): void => {
  e.preventDefault();
};

export const exitEditMode = (): void => {
  viewStateVar({
    ...viewStateVar(),
    editClip: null,
    mode: VISUAL_MODE,
  });
};

export const disableContextMenuElem = (elem: HTMLElement): void => {
  elem.addEventListener('contextmenu', contextMenuDisable);
};

interface ViewModelState {
  viewModels?: {
    library?: Record<string, { node: unknown }>;
  };
}

export const getViewModel = (state: ViewModelState, name: string): unknown => {
  return get(state, `viewModels.library.${name}.node`);
};

export const tryJsonParse = <T = unknown>(val: string): T | null => {
  try {
    return JSON.parse(val) as T;
  } catch {
    return null;
  }
};

interface ProxyFormatObject {
  node?: {
    mimetype?: string;
  };
}

type MediaType = typeof VIDEO | typeof AUDIO | typeof IMAGE | typeof PDF | undefined;

export const determineMediaType = (proxyFmtObj: ProxyFormatObject | null | undefined): MediaType => {
  const mimetype = proxyFmtObj?.node?.mimetype;
  if (!mimetype) return undefined;

  if (mimetype.includes(VIDEO)) {
    return VIDEO;
  } else if (mimetype.includes('application/mxf')) {
    return VIDEO;
  } else if (mimetype.includes(AUDIO)) {
    return AUDIO;
  } else if (mimetype.includes(IMAGE)) {
    return IMAGE;
  } else if (mimetype.includes(PDF)) {
    return PDF;
  }
  return undefined;
};

export const getCurrIdxByItemId = (itemID: string, itemIDs: string[]): number => {
  return itemIDs.indexOf(itemID);
};

export const downloadCollectionItemFromDownloadURL = (downloadPath: string): void => {
  let url: URL | null = null;

  try {
    url = new URL(downloadPath);
  } catch (e) {
    if (e instanceof TypeError && e.message.includes('URL')) {
      url = new URL(downloadPath, window.location.origin);
    }
  } finally {
    if (!url || !String(url)) {
      toast.error(`Unable to download item: ${downloadPath} - invalid download URL`);
    }
  }

  if (url !== null) {
    const atag = document.createElement('a');
    atag.setAttribute('href', String(url));
    atag.setAttribute('download', String(url));
    atag.click();
    atag.remove();
  }
};

interface CollectionItemNode {
  node: {
    contentObject: {
      id: string;
    };
  };
}

export const getCollectionItemGlobalIds = (collectionItems: CollectionItemNode[]): string[] => {
  return collectionItems.map(({ node }) => node.contentObject.id);
};

interface DownloadLink {
  globalId: string;
  downloadPath: string;
}

interface DownloadLinksData {
  downloadLinks?: DownloadLink[];
  sharedDownloadLinks?: DownloadLink[];
}

export const downloadCollectionItemsAsync = async (
  globalIds: string[],
  downloadsLinkLazyQueryFunc: LazyQueryExecFunction<DownloadLinksData, OperationVariables>,
  shared = false,
): Promise<void> => {
  const { data } = await downloadsLinkLazyQueryFunc({
    variables: {
      globalIds,
    },
  });

  if (data) {
    const key = shared ? 'sharedDownloadLinks' : 'downloadLinks';
    const links = data[key];

    for (const globalId of globalIds) {
      const downloadLink = links?.find((link) => link.globalId === globalId);
      if (downloadLink?.downloadPath) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        await downloadCollectionItemFromDownloadURL(downloadLink.downloadPath);
      }
    }
  }
};

interface Owner {
  firstName?: string;
  lastName?: string;
  user?: {
    username?: string;
  };
}

export const getOwner = (owner: Owner | null | undefined): string | undefined => {
  return owner?.firstName || owner?.lastName
    ? `${owner?.firstName || ''} ${owner?.lastName || ''}`.trim()
    : owner?.user?.username;
};

interface ContentObject {
  id: string;
  __typename?: string;
}

interface OpensearchResult {
  contentObject: ContentObject;
}

interface OpensearchData {
  opensearch?: {
    results: OpensearchResult[];
  };
}

export const getCurrentOsResultById = (
  id: string,
  osResults: OpensearchData | null | undefined,
): OpensearchResult | undefined => {
  return osResults?.opensearch?.results.find((res) => res?.contentObject.id === id);
};

const DEFAULT = 'ASSET';

export const getOSResultTypeNameById = (id: string, osResults: OpensearchData | null | undefined): string => {
  const currentItem = getCurrentOsResultById(id, osResults);
  if (currentItem) {
    return currentItem.contentObject?.__typename || DEFAULT;
  }
  return DEFAULT;
};

interface CollectionItem {
  contentObject: ContentObject;
}

export const getCollectionTypeNameById = (id: string, items: CollectionItem[]): string => {
  const currentItem = items.find((item) => item.contentObject.id === id);
  if (currentItem) {
    return currentItem.contentObject?.__typename || DEFAULT;
  }
  return DEFAULT;
};

interface ThumbnailFormatVars {
  asset_Id?: string;
  asset_Items_ObjectId?: string;
  fmtType_Slug: string;
}

export const getThumbnailFormatVarsByItemType = (id: string, itemType: string): ThumbnailFormatVars | undefined => {
  if (itemType.includes('Asset')) {
    return { asset_Id: id, fmtType_Slug: THUMBNAIL_FORMAT_SLUG };
  }
  if (itemType.includes('Collection')) {
    return { asset_Items_ObjectId: id, fmtType_Slug: THUMBNAIL_FORMAT_SLUG };
  }
  return undefined;
};

interface CreateGallerySuccessParams {
  navigate: UseNavigateResult<string>;
  galleryId: string;
}

export const onCreateGallerySuccess = ({ navigate, galleryId }: CreateGallerySuccessParams): void => {
  navigate({ to: `${HOME_PATH}/gallery/${galleryId}` });
};

interface CreateGalleryErrorParams {
  error: Error;
}

export const onErrorCreateGallery = ({ error }: CreateGalleryErrorParams): void => {
  const errorMess = error?.message?.includes('duplicate key value violates unique constraint')
    ? 'Gallery already exists'
    : error?.message;

  toast.error(errorMess || 'Unknown error');
};

export function timecodeToSeconds(timecode: string): number {
  const normalizedTimecode = timecode.replace(';', ':');
  const [hours, minutes, seconds, milliseconds] = normalizedTimecode.split(':').map(Number);
  return hours * 3600 + minutes * 60 + seconds + milliseconds / 1000;
}
